import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Drawer,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
  Badge,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import PeopleIcon from "@mui/icons-material/People";
import WifiIcon from "@mui/icons-material/Wifi";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { io } from "socket.io-client";
import api from "../../api/api";

const openGoogleMaps = (lat, lng) =>
  window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");

/* ================================================================
   CSS INJECTION — pulse animations + marker styles
================================================================ */
const MAP_STYLES = `
  .mtaani-marker-online {
    width: 20px; height: 20px;
    background: #22c55e;
    border-radius: 50%;
    border: 3px solid #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    animation: mtaani-pulse 2s infinite;
    cursor: pointer;
  }
  .mtaani-marker-offline {
    width: 16px; height: 16px;
    background: #94a3b8;
    border-radius: 50%;
    border: 3px solid #fff;
    box-shadow: 0 1px 4px rgba(0,0,0,0.25);
    cursor: pointer;
  }
  @keyframes mtaani-pulse {
    0%   { box-shadow: 0 0 0 0   rgba(34,197,94,0.55); }
    70%  { box-shadow: 0 0 0 14px rgba(34,197,94,0); }
    100% { box-shadow: 0 0 0 0   rgba(34,197,94,0); }
  }
  .leaflet-popup-content-wrapper {
    border-radius: 14px !important;
    box-shadow: 0 8px 30px rgba(0,0,0,0.18) !important;
    padding: 0 !important;
    overflow: hidden;
  }
  .leaflet-popup-content { margin: 0 !important; }
  .leaflet-popup-tip-container { display: none; }
`;

/* ================================================================
   MARKER ICONS
================================================================ */
const onlineIcon = L.divIcon({
  html: `<div class="mtaani-marker-online"></div>`,
  className: "",
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -14],
});

const offlineIcon = L.divIcon({
  html: `<div class="mtaani-marker-offline"></div>`,
  className: "",
  iconSize: [16, 16],
  iconAnchor: [8, 8],
  popupAnchor: [0, -12],
});

/* ================================================================
   FLY TO HELPER
================================================================ */
function FlyToTarget({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) {
      map.flyTo([target.lat, target.lng], 16, { duration: 1.4, easeLinearity: 0.25 });
    }
  }, [target, map]);
  return null;
}

/* ================================================================
   POPUP CARD
================================================================ */
function PopupCard({ cust, orderCount, lat, lng }) {
  return (
    <Box sx={{ width: 220, fontFamily: "inherit" }}>
      <Box
        sx={{
          background: cust.isOnline
            ? "linear-gradient(135deg,#16a34a,#22c55e)"
            : "linear-gradient(135deg,#475569,#64748b)",
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Avatar sx={{ bgcolor: "rgba(255,255,255,0.25)", width: 38, height: 38 }}>
          <PersonIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Avatar>
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>
            {cust.name}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: 11 }}>
            {cust.isOnline ? "● Online now" : "● Offline"}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ p: 1.5, background: "#fff" }}>
        {cust.email && (
          <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.5 }}>
            {cust.email}
          </Typography>
        )}
        {cust.phone && (
          <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.5 }}>
            {cust.phone}
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}>
          <ShoppingBagIcon sx={{ fontSize: 14, color: "#667eea" }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>
            {orderCount} order{orderCount !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Box
          onClick={() => openGoogleMaps(lat, lng)}
          sx={{
            mt: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "pointer",
            color: "#1a73e8",
            fontSize: 12,
            fontWeight: 600,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <OpenInNewIcon sx={{ fontSize: 13 }} />
          Open in Google Maps
        </Box>
      </Box>
    </Box>
  );
}

/* ================================================================
   MAIN COMPONENT
================================================================ */
export default function CustomerMapDashboard() {
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [liveLocations, setLiveLocations] = useState({});
  const [search, setSearch] = useState("");
  const [flyTarget, setFlyTarget] = useState(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orderDrawerOpen, setOrderDrawerOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const socketRef = useRef(null);

  /* inject CSS once */
  useEffect(() => {
    const tag = document.createElement("style");
    tag.textContent = MAP_STYLES;
    document.head.appendChild(tag);
    return () => document.head.removeChild(tag);
  }, []);

  /* load data */
  useEffect(() => {
    api.get("/api/admin/customers/with-orders").then((res) => {
      setCustomers(res.data.customers || []);
      setOrders(res.data.orders || []);
    });
  }, []);

  /* sockets */
  useEffect(() => {
    const baseURL = api.defaults.baseURL.replace(/\/+$/, "");
    const socket = io(baseURL, { transports: ["websocket"] });
    socketRef.current = socket;

    socket.on("customer:location", ({ userId, lat, lng }) => {
      setLiveLocations((prev) => ({ ...prev, [userId]: { lat, lng } }));
    });
    socket.on("customer:online", ({ userId }) =>
      setCustomers((prev) => prev.map((c) => (c._id === userId ? { ...c, isOnline: true } : c)))
    );
    socket.on("customer:offline", ({ userId }) =>
      setCustomers((prev) => prev.map((c) => (c._id === userId ? { ...c, isOnline: false } : c)))
    );

    return () => socket.disconnect();
  }, []);

  /* helpers */
  const getLocation = useCallback(
    (cust) => liveLocations[cust._id] || cust.currentLocation || null,
    [liveLocations]
  );

  const orderCount = useCallback(
    (custId) => orders.filter((o) => o.user?._id === custId).length,
    [orders]
  );

  /* stats */
  const stats = useMemo(() => {
    const online = customers.filter((c) => c.isOnline).length;
    const withLoc = customers.filter((c) => getLocation(c)?.lat).length;
    return { total: customers.length, online, withLoc };
  }, [customers, getLocation]);

  /* sorted + filtered customer list (online first) */
  const filteredCustomers = useMemo(() => {
    const q = search.toLowerCase();
    return [...customers]
      .filter((c) => `${c.name} ${c.email} ${c.phone || ""}`.toLowerCase().includes(q))
      .sort((a, b) => (b.isOnline ? 1 : 0) - (a.isOnline ? 1 : 0));
  }, [customers, search]);

  /* customers that have a location (shown on map) */
  const mappedCustomers = useMemo(
    () => customers.filter((c) => getLocation(c)?.lat),
    [customers, getLocation]
  );

  /* fly to customer */
  const flyTo = (cust) => {
    const loc = getLocation(cust);
    if (!loc?.lat) return;
    setFlyTarget({ lat: loc.lat, lng: loc.lng });
  };

  /* open order drawer */
  const openOrderDrawer = async (orderId) => {
    try {
      const res = await api.get(`/api/orders/${orderId}`);
      setSelectedOrder(res.data);
      setOrderDrawerOpen(true);
    } catch (err) {
      console.error("Failed to load order", err);
    }
  };

  /* ── shared glass panel sx ── */
  const glass = {
    background: "rgba(255,255,255,0.92)",
    backdropFilter: "blur(12px)",
    borderRadius: 3,
    boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
    border: "1px solid rgba(255,255,255,0.6)",
  };

  /* ================================================================
     RENDER
  ================================================================ */
  return (
    <Box sx={{ position: "relative", height: "calc(100vh - 64px)", overflow: "hidden" }}>

      {/* ── MAP (full background) ── */}
      <MapContainer
        center={[-1.286389, 36.817223]} /* Nairobi default */
        zoom={12}
        style={{ width: "100%", height: "100%", zIndex: 0 }}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
          maxZoom={20}
        />

        {flyTarget && <FlyToTarget target={flyTarget} />}

        {mappedCustomers.map((cust) => {
          const loc = getLocation(cust);
          return (
            <Marker
              key={cust._id}
              position={[loc.lat, loc.lng]}
              icon={cust.isOnline ? onlineIcon : offlineIcon}
            >
              <Popup>
                <PopupCard cust={cust} orderCount={orderCount(cust._id)} lat={loc.lat} lng={loc.lng} />
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>

      {/* ── TOP STATS BAR ── */}
      <Box
        sx={{
          position: "absolute",
          top: 16,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 900,
          display: "flex",
          gap: 1,
          ...glass,
          px: 2,
          py: 1,
        }}
      >
        <Chip
          icon={<PeopleIcon sx={{ fontSize: 16 }} />}
          label={`${stats.total} customers`}
          size="small"
          sx={{ fontWeight: 600, bgcolor: "transparent" }}
        />
        <Chip
          icon={<WifiIcon sx={{ fontSize: 16 }} />}
          label={`${stats.online} online`}
          size="small"
          color="success"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
        <Chip
          icon={<LocationOnIcon sx={{ fontSize: 16 }} />}
          label={`${stats.withLoc} located`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {/* ── PANEL TOGGLE BUTTON ── */}
      <IconButton
        onClick={() => setPanelOpen((v) => !v)}
        sx={{
          position: "absolute",
          top: "50%",
          left: panelOpen ? 314 : 8,
          transform: "translateY(-50%)",
          zIndex: 1000,
          transition: "left 0.3s ease",
          ...glass,
          width: 28,
          height: 52,
          borderRadius: "0 8px 8px 0",
          "&:hover": { background: "rgba(255,255,255,0.98)" },
        }}
      >
        {panelOpen ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
      </IconButton>

      {/* ── LEFT PANEL ── */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: panelOpen ? 0 : -310,
          width: 310,
          height: "100%",
          zIndex: 900,
          transition: "left 0.3s ease",
          display: "flex",
          flexDirection: "column",
          ...glass,
          borderRadius: "0 16px 16px 0",
          borderLeft: "none",
        }}
      >
        {/* Panel header */}
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5 }}>
            Customer Tracking
          </Typography>

          {/* Search */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              background: "#f1f5f9",
              borderRadius: 2,
              px: 1.5,
              py: 0.75,
              gap: 1,
            }}
          >
            <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer…"
              style={{
                border: "none",
                background: "transparent",
                outline: "none",
                fontSize: 13,
                flex: 1,
                fontFamily: "inherit",
                color: "#334155",
              }}
            />
            {search && (
              <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0 }}>
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        <Divider />

        {/* Customer list */}
        <Box sx={{ flex: 1, overflowY: "auto", py: 0.5 }}>
          {filteredCustomers.length === 0 && (
            <Typography sx={{ p: 3, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
              No customers found
            </Typography>
          )}

          {filteredCustomers.map((cust) => {
            const loc = getLocation(cust);
            const hasLoc = !!loc?.lat;
            const cnt = orderCount(cust._id);

            return (
              <Box
                key={cust._id}
                onClick={() => {
                  setSelectedCustomer(cust);
                  flyTo(cust);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  py: 1.25,
                  cursor: "pointer",
                  transition: "background 0.15s",
                  background:
                    selectedCustomer?._id === cust._id
                      ? "rgba(102,126,234,0.1)"
                      : "transparent",
                  "&:hover": { background: "rgba(102,126,234,0.07)" },
                  borderLeft:
                    selectedCustomer?._id === cust._id
                      ? "3px solid #667eea"
                      : "3px solid transparent",
                }}
              >
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  badgeContent={
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: "50%",
                        background: cust.isOnline ? "#22c55e" : "#94a3b8",
                        border: "1.5px solid #fff",
                      }}
                    />
                  }
                >
                  <Avatar sx={{ width: 36, height: 36, bgcolor: cust.isOnline ? "#dcfce7" : "#f1f5f9" }}>
                    <PersonIcon sx={{ fontSize: 18, color: cust.isOnline ? "#16a34a" : "#94a3b8" }} />
                  </Avatar>
                </Badge>

                <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3, color: "#1e293b" }}
                    noWrap
                  >
                    {cust.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>
                    {cnt} order{cnt !== 1 ? "s" : ""}
                    {cust.phone ? ` · ${cust.phone}` : ""}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
                  {cnt > 0 && (
                    <Tooltip title="View orders">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCustomer(cust);
                          setOrderDrawerOpen(true);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <ShoppingBagIcon sx={{ fontSize: 15, color: "#667eea" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasLoc && (
                    <Tooltip title="Fly to location">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          flyTo(cust);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <MyLocationIcon sx={{ fontSize: 15, color: "#22c55e" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasLoc && (
                    <Tooltip title="Open in Google Maps">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openGoogleMaps(loc.lat, loc.lng);
                        }}
                        sx={{ p: 0.5 }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 15, color: "#1a73e8" }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Panel footer */}
        <Divider />
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
            Click a customer to fly to their location
          </Typography>
        </Box>
      </Box>

      {/* ── ORDER DRAWER ── */}
      <Drawer
        anchor="right"
        open={orderDrawerOpen}
        onClose={() => setOrderDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 400,
            p: 0,
            background: "#fff",
            boxShadow: "0 0 40px rgba(0,0,0,0.15)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: "linear-gradient(135deg,#667eea,#764ba2)",
            p: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 16 }}>
              {selectedCustomer?.name}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: 12 }}>
              Order History
            </Typography>
          </Box>
          <IconButton onClick={() => setOrderDrawerOpen(false)} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Order list */}
        <Box sx={{ p: 2, overflowY: "auto", height: "calc(100% - 76px)" }}>
          {selectedCustomer &&
            orders
              .filter((o) => o.user?._id === selectedCustomer._id)
              .map((order) => (
                <Box
                  key={order._id}
                  onClick={() => openOrderDrawer(order._id)}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 2.5,
                    p: 2,
                    mb: 1.5,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": {
                      border: "1px solid #667eea",
                      background: "rgba(102,126,234,0.04)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                      #{order._id.slice(-6).toUpperCase()}
                    </Typography>
                    <Chip
                      label={order.status}
                      size="small"
                      color={
                        order.status === "completed"
                          ? "success"
                          : order.status === "cancelled"
                          ? "error"
                          : "primary"
                      }
                      sx={{ fontWeight: 600, fontSize: 11 }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                    {order.items?.length || 0} items
                  </Typography>
                </Box>
              ))}

          {selectedCustomer &&
            orders.filter((o) => o.user?._id === selectedCustomer._id).length === 0 && (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <ShoppingBagIcon sx={{ fontSize: 48, color: "#e2e8f0", mb: 1 }} />
                <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>No orders yet</Typography>
              </Box>
            )}
        </Box>
      </Drawer>
    </Box>
  );
}
